/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import { isSameDate as defaultIsSameDate, getSafeTime as defaultGetSafeTime } from '../lib/helpers';

// Helper to parse ordered_by and message from order notes
const defaultParseOrderNote = (note) => {
  if (!note) return { orderedBy: 'ไม่ระบุ', noteText: '' };
  if (typeof note === 'string' && note.startsWith("ordered_by:")) {
    const pipeIdx = note.indexOf("|note:");
    if (pipeIdx !== -1) {
      const orderedBy = note.substring(11, pipeIdx);
      const noteText = note.substring(pipeIdx + 6);
      return { orderedBy, noteText };
    }
  }
  return { orderedBy: 'ไม่ระบุ', noteText: note };
};

// Helper to get display name by email
const defaultGetUserNameByEmail = (email) => {
  if (!email) return 'ไม่ระบุ';
  if (email === 'guest@hospital.com') return 'Guest User';
  return email;
};

// Fallback timeline generator if none provided
const defaultGetTimeline = (instr, barcode, currentLogs = []) => {
  const steps = [];
  const addStep = (name, timestamp, details = {}) => {
    if (steps.length > 0 && steps[steps.length - 1].name === name) {
      return;
    }
    steps.push({ name, timestamp, ...details });
  };

  const targetBarcode = barcode || (instr ? instr.unique_barcode : null);

  if (targetBarcode) {
    const barcodeLogs = currentLogs.filter(l => l.action && l.action.includes(targetBarcode));
    const sortedLogs = [...barcodeLogs].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    sortedLogs.forEach(l => {
      const wh = (l.warehouse || "").trim();
      const act = l.action || "";

      if (wh.includes("ล้าง")) {
        addStep("ห้องล้าง", l.timestamp, { action: act, warehouse: wh });
      } else if (act.includes("นึ่ง") || act.includes("sterile-ready")) {
        addStep("ห้องนึ่ง", l.timestamp, { action: act, warehouse: wh });
      } else if (wh.includes("sterile")) {
        addStep("ห้อง supply", l.timestamp, { action: act, warehouse: wh });
      } else {
        addStep(wh || "อื่นๆ", l.timestamp, { action: act, warehouse: wh });
      }
    });
  }

  if (instr) {
    if (steps.length === 0) {
      if (instr.status === 'sterile-pending') {
        addStep("ห้องล้าง", instr.last_updated);
      } else if (instr.status === 'sterile-ready') {
        addStep("ห้องล้าง", instr.last_updated);
        addStep("ห้องนึ่ง", instr.last_updated);
        addStep("ห้อง supply", instr.last_updated);
      } else if (instr.status === 'in-use') {
        addStep("ห้องล้าง", instr.last_updated);
        addStep("ห้องนึ่ง", instr.last_updated);
        addStep("ห้อง supply", instr.last_updated);
        const clinicName = (instr.location || "").replace(/^\s*-\s*/, "").trim() || "ห้องฟัน";
        addStep(clinicName, instr.last_updated);
      }
    } else {
      const lastStepName = steps[steps.length - 1].name;
      if (instr.status === 'sterile-ready') {
        if (lastStepName === "ห้องนึ่ง") {
          addStep("ห้อง supply", instr.last_updated);
        }
      } else if (instr.status === 'in-use') {
        if (lastStepName === "ห้องนึ่ง") {
          addStep("ห้อง supply", instr.last_updated);
        }
        const clinicName = (instr.location || "").replace(/^\s*-\s*/, "").trim() || "ห้องฟัน";
        addStep(clinicName, instr.last_updated);
      }
    }
  } else {
    if (steps.length === 0) {
      addStep("ห้อง supply", new Date().toISOString());
    }
  }

  return steps;
};

const SupplyPage = ({
  actionType = 'receive-item',
  setActionType,
  sterileSuccessList = [],
  setSterileSuccessList,
  sterileProblemList = [],
  setSterileProblemList,
  targetBox = null,
  setTargetBox,
  barcodeInput = '',
  setBarcodeInput,
  scanErrorMessage = '',
  setScanErrorMessage,
  isSterileModalOpen = false,
  setIsSterileModalOpen,
  isDispenseModalOpen = false,
  setIsDispenseModalOpen,
  selectedOrder = null,
  setSelectedOrder,
  targetWarehouse = '',
  setTargetWarehouse,
  handleCloseSterileModal,
  preparedBy = '',
  setPreparedBy,
  checkedBy = '',
  setCheckedBy,
  sterileMachine = '',
  setSterileMachine,
  sterileCycle = '',
  setSterileCycle,
  sterileOptions = {},
  mfgDate = '',
  setMfgDate,
  expDate = '',
  setExpDate,
  barcodeInputRef,
  handleBlurToFocus,
  handleSterileScan,
  handleDispenseScan,
  setCameraTargetField,
  setIsCameraOpen,
  handleConfirmSterileBasket,
  instruments = [],
  supplies = [],
  itemMaster = [],
  instrumentTags = [],
  logs = [],
  orders = [],
  handleCancelOrder,
  showPreview = {},
  setShowPreview,
  handleTogglePreview,
  sterileHistoryDate = '',
  setSterileHistoryDate,
  isSameDate = defaultIsSameDate,
  getSafeTime = defaultGetSafeTime,
  parseOrderNote = defaultParseOrderNote,
  getUserNameByEmail = defaultGetUserNameByEmail,
  getTimeline,
  sterileErrorAlert = null,
  setSterileErrorAlert,
  selectedTimelineItem = null,
  setSelectedTimelineItem,
  setShowAddOptionModal,
  setNewOptionCategory,
  setNewOptionValue,
  setIsSavingOption,
  setShowManageOptionsModal,
  setSelectedManageCategory,
}) => {
  // Safe fallbacks for helpers
  const checkSameDate = isSameDate || defaultIsSameDate;
  const parseNote = parseOrderNote || defaultParseOrderNote;
  const getName = getUserNameByEmail || defaultGetUserNameByEmail;
  const getTime = getSafeTime || defaultGetSafeTime;

  // Safe fallback states for timeline detail and error alert if not controlled
  const [internalTimelineItem, setInternalTimelineItem] = useState(null);
  const activeTimelineItem = selectedTimelineItem !== undefined ? selectedTimelineItem : internalTimelineItem;
  const updateTimelineItem = setSelectedTimelineItem || setInternalTimelineItem;

  const [internalErrorAlert, setInternalErrorAlert] = useState(null);
  const activeSterileErrorAlert = sterileErrorAlert !== undefined ? sterileErrorAlert : internalErrorAlert;
  const updateErrorAlert = setSterileErrorAlert || setInternalErrorAlert;

  const resolveTimeline = (instr, barcode) => {
    if (getTimeline) {
      return getTimeline(instr, barcode);
    }
    return defaultGetTimeline(instr, barcode, logs);
  };

  const onCloseSterileModal = () => {
    if (handleCloseSterileModal) {
      handleCloseSterileModal();
    } else {
      if (setIsSterileModalOpen) setIsSterileModalOpen(false);
      if (setSterileSuccessList) setSterileSuccessList([]);
      if (setSterileProblemList) setSterileProblemList([]);
      if (setTargetBox) setTargetBox(null);
      if (setBarcodeInput) setBarcodeInput('');
      if (setScanErrorMessage) setScanErrorMessage('');
      if (updateErrorAlert) updateErrorAlert(null);
    }
  };

  const onCloseDispenseModal = () => {
    if (setIsDispenseModalOpen) setIsDispenseModalOpen(false);
    if (setSterileSuccessList) setSterileSuccessList([]);
    if (setSelectedOrder) setSelectedOrder(null);
    if (setTargetWarehouse) setTargetWarehouse('');
    if (setBarcodeInput) setBarcodeInput('');
    if (setScanErrorMessage) setScanErrorMessage('');
  };

  return (
    <div className="sterile-container">
      <div className="sterile-card">
        {/* Header */}
        <div className="sterile-header">
          <div style={{ fontSize: '3.5em', marginBottom: '10px', filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))' }}>🏥</div>
          <h2>หน้างานห้อง Sterile (Basket Mode)</h2>
          <p>จัดการขั้นตอนการนึ่งฆ่าเชื้อ การรับคืนกล่อง และจัดเครื่องมือลงเซ็ตกล่องแพทย์</p>
          <div className="accent-bar" />
        </div>

        {/* Mode Selector */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '25px' }}>
          <button
            onClick={() => {
              if (setActionType) setActionType('receive-item');
              if (setSterileSuccessList) setSterileSuccessList([]);
              if (setSterileProblemList) setSterileProblemList([]);
              if (setTargetBox) setTargetBox(null);
              if (setBarcodeInput) setBarcodeInput('');
              if (setScanErrorMessage) setScanErrorMessage('');
              if (setIsSterileModalOpen) setIsSterileModalOpen(true);
            }}
            className={`sterile-mode-btn ${actionType === 'receive-item' ? 'active-receive' : ''}`}
            style={{ width: '100%', padding: '18px', fontSize: '1.15em' }}
          >
            📥 รับของเข้าห้อง / รับกล่องคืน
          </button>
          <button
            onClick={() => {
              if (setActionType) setActionType('decrease');
              if (setSterileSuccessList) setSterileSuccessList([]);
              if (setSterileProblemList) setSterileProblemList([]);
              if (setSelectedOrder) setSelectedOrder(null);
              if (setTargetWarehouse) setTargetWarehouse('');
              if (setBarcodeInput) setBarcodeInput('');
              if (setScanErrorMessage) setScanErrorMessage('');
              if (setIsDispenseModalOpen) setIsDispenseModalOpen(true);
            }}
            className={`sterile-mode-btn active-dispense-merged`}
            style={{ width: '100%', padding: '18px', fontSize: '1.15em' }}
          >
            📤 จ่ายของ (ห้องจ่ายเครื่องมือ)
          </button>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button
              onClick={() => {
                if (setActionType) setActionType('bind-box');
                if (setSterileSuccessList) setSterileSuccessList([]);
                if (setSterileProblemList) setSterileProblemList([]);
                if (setTargetBox) setTargetBox(null);
                if (setBarcodeInput) setBarcodeInput('');
                if (setScanErrorMessage) setScanErrorMessage('');
                if (setIsSterileModalOpen) setIsSterileModalOpen(true);
              }}
              className={`sterile-mode-btn ${actionType === 'bind-box' ? 'active-bind-box' : ''}`}
              style={{ flex: 1 }}
            >
              🧰 จัดหัวกรอลงกล่อง
            </button>
            <button
              onClick={() => {
                if (setActionType) setActionType('auto-pack');
                if (setSterileSuccessList) setSterileSuccessList([]);
                if (setSterileProblemList) setSterileProblemList([]);
                if (setTargetBox) setTargetBox(null);
                if (setBarcodeInput) setBarcodeInput('');
                if (setScanErrorMessage) setScanErrorMessage('');
                if (setIsSterileModalOpen) setIsSterileModalOpen(true);
              }}
              className={`sterile-mode-btn ${actionType === 'auto-pack' ? 'active-auto-pack' : ''}`}
              style={{ flex: 1 }}
            >
              🧲 ยิงเข้ากล่อง (Auto)
            </button>
          </div>
        </div>

      </div>

      {/* Popup Modal Overlay */}
      {isSterileModalOpen && (
        <div className="sterile-modal-overlay" onClick={onCloseSterileModal}>
          <div className="sterile-modal-content" onClick={(e) => e.stopPropagation()}>

            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '15px' }}>
              <h3 style={{ margin: 0, fontSize: '1.4em', fontWeight: '700', color: '#fff' }}>
                {actionType === 'receive-item' && '📥 รับของเข้าห้อง / รับกล่องคืน'}
                {actionType === 'bind-box' && '🧰 จัดหัวกรอลงกล่อง'}
                {actionType === 'auto-pack' && '🧲 ยิงเข้ากล่อง (Auto)'}
              </h3>
              <button
                onClick={onCloseSterileModal}
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: 'none',
                  color: 'rgba(255, 255, 255, 0.75)',
                  fontSize: '1.2em',
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(231, 76, 60, 0.2)'; e.currentTarget.style.color = '#e74c3c'; }}
                onMouseOut={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)'; e.currentTarget.style.color = 'rgba(255, 255, 255, 0.75)'; }}
              >
                ✕
              </button>
            </div>

            {/* Bind Box Template Section */}
            {actionType === 'bind-box' && targetBox && (() => {
              const boxTemplate = itemMaster.find(m => m.id === targetBox.master_id);
              const requiredItemsMap = {};
              boxTemplate?.components?.forEach(compId => requiredItemsMap[compId] = (requiredItemsMap[compId] || 0) + 1);

              const itemsAlreadyInBox = instruments.filter(i => i.container_code === targetBox.unique_barcode && i.is_active);
              const allActualItems = [...itemsAlreadyInBox, ...sterileSuccessList];
              const actualItemsMap = {};
              allActualItems.forEach(inst => {
                actualItemsMap[inst.master_id] = (actualItemsMap[inst.master_id] || 0) + 1;
              });

              return (
                <div style={{
                  background: 'rgba(0, 150, 136, 0.1)',
                  border: '1px solid rgba(0, 150, 136, 0.25)',
                  padding: '20px',
                  borderRadius: '16px',
                  marginBottom: '20px',
                  backdropFilter: 'blur(10px)',
                  animation: 'fadeInUp 0.4s ease-out'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', borderBottom: '1px solid rgba(0, 150, 136, 0.2)', paddingBottom: '10px', flexWrap: 'wrap', gap: '10px' }}>
                    <h3 style={{ margin: 0, color: '#26a69a', fontSize: '1.15em', fontWeight: '600' }}>
                      📦 กำลังจัดกล่อง: {targetBox.item_name} &nbsp;
                      <span style={{ fontSize: '0.8em', background: '#004d40', color: '#80cbc4', padding: '4px 10px', borderRadius: '20px', fontWeight: 'bold' }}>{targetBox.unique_barcode}</span>
                    </h3>
                    <button
                      onClick={() => {
                        if (setTargetBox) setTargetBox(null);
                        if (setSterileSuccessList) setSterileSuccessList([]);
                      }}
                      style={{
                        background: 'rgba(231, 76, 60, 0.15)',
                        border: '1px solid rgba(231, 76, 60, 0.3)',
                        color: '#e74c3c',
                        borderRadius: '8px',
                        padding: '8px 16px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '0.85em',
                        transition: 'all 0.2s'
                      }}
                    >
                      ❌ ยกเลิก / เปลี่ยนกล่อง
                    </button>
                  </div>

                  <h4 style={{ margin: '0 0 12px 0', color: '#80cbc4', fontSize: '0.95em', fontWeight: '500' }}>📋 รายการหัวกรอที่ต้องใส่ในกล่องนี้:</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '10px' }}>
                    {Object.keys(requiredItemsMap).length === 0 ? (
                      <div style={{ color: '#ff9800', padding: '10px', fontSize: '0.9em' }}>⚠️ กล่องนี้ยังไม่ได้ตั้งค่าแม่แบบ (กรุณาไปตั้งค่าที่หน้า "กล่องหมอ")</div>
                    ) : (
                      Object.keys(requiredItemsMap).map(compId => {
                        const compMaster = itemMaster.find(i => i.id === compId);
                        if (!compMaster) return null;

                        const reqQty = requiredItemsMap[compId];
                        const actualQty = actualItemsMap[compId] || 0;
                        const isComplete = actualQty >= reqQty;

                        return (
                          <div
                            key={compId}
                            style={{
                              background: isComplete ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 255, 255, 0.03)',
                              border: isComplete ? '1px solid rgba(76, 175, 80, 0.3)' : '1px solid rgba(255, 255, 255, 0.08)',
                              padding: '12px 15px',
                              borderRadius: '10px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              transition: 'all 0.3s'
                            }}
                          >
                            <span style={{ fontWeight: isComplete ? '600' : 'normal', color: isComplete ? '#81c784' : 'rgba(255,255,255,0.85)', fontSize: '0.9em' }}>
                              🦷 {compMaster.item_name}
                            </span>
                            <span style={{ fontWeight: '700', fontSize: '1.1em', color: isComplete ? '#81c784' : '#ffb74d' }}>
                              {actualQty} / {reqQty} {isComplete && '✅'}
                            </span>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Barcode Input & Scanner */}
            <div className="sterile-input-group">
              <input
                type="text"
                ref={barcodeInputRef}
                onBlur={handleBlurToFocus}
                value={barcodeInput}
                onChange={(e) => {
                  if (setBarcodeInput) setBarcodeInput(e.target.value);
                  if (e.target.value.length === 13 || e.target.value.length === 14) {
                    if (handleSterileScan) handleSterileScan(e.target.value);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (barcodeInput.trim() !== '' && barcodeInput.length !== 13 && barcodeInput.length !== 14) {
                      if (handleSterileScan) handleSterileScan(barcodeInput.trim());
                    }
                  }
                }}
                placeholder={actionType === 'bind-box' && !targetBox ? "สแกนรหัสกล่อง (BOX-...) ก่อนครับ" : "สแกนรหัส 13 หรือ 14 หลัก..."}
                className="sterile-input"
              />
              <button
                onClick={() => {
                  if (setCameraTargetField) setCameraTargetField('main');
                  if (setIsCameraOpen) setIsCameraOpen(true);
                }}
                className="sterile-camera-btn"
                title="เปิดกล้องสแกน"
              >
                📷
              </button>
            </div>

            {/* Success List */}
            <div className="sterile-list-container">
              <div className="sterile-list-card ready" style={{ width: '100%' }}>
                <div className="sterile-list-card-header ready" style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '15px', marginBottom: '15px' }}>
                  <h4 style={{ margin: 0, color: '#fff', fontSize: '1.2em' }}>📋 สรุปรายการสแกน</h4>

                  <div style={{ display: 'flex', gap: '10px', width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 30%', display: 'flex', flexDirection: 'column', alignItems: 'center', background: 'rgba(255, 255, 255, 0.05)', padding: '8px 4px', borderRadius: '10px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <span style={{ fontSize: '0.75em', color: 'rgba(255, 255, 255, 0.6)', fontWeight: '600', marginBottom: '2px' }}>สแกนทั้งหมด</span>
                      <span style={{ fontSize: '1.6em', fontWeight: '800', color: '#fff', fontFamily: 'monospace', lineHeight: '1' }}>
                        {sterileSuccessList.length + sterileProblemList.length}
                      </span>
                    </div>

                    <div style={{ flex: '1 1 30%', display: 'flex', flexDirection: 'column', alignItems: 'center', background: 'rgba(46, 204, 113, 0.15)', padding: '8px 4px', borderRadius: '10px', border: '1px solid rgba(46, 204, 113, 0.3)' }}>
                      <span style={{ fontSize: '0.75em', color: '#2ecc71', fontWeight: '600', marginBottom: '2px' }}>สแกนได้</span>
                      <span style={{ fontSize: '1.6em', fontWeight: '800', color: '#2ecc71', fontFamily: 'monospace', lineHeight: '1' }}>
                        {sterileSuccessList.length}
                      </span>
                    </div>

                    <div style={{ flex: '1 1 30%', display: 'flex', flexDirection: 'column', alignItems: 'center', background: 'rgba(231, 76, 60, 0.15)', padding: '8px 4px', borderRadius: '10px', border: '1px solid rgba(231, 76, 60, 0.3)' }}>
                      <span style={{ fontSize: '0.75em', color: '#e74c3c', fontWeight: '600', marginBottom: '2px' }}>มีปัญหา</span>
                      <span style={{ fontSize: '1.6em', fontWeight: '800', color: '#e74c3c', fontFamily: 'monospace', lineHeight: '1' }}>
                        {sterileProblemList.length}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="sterile-list-items" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                  {sterileSuccessList.length === 0 && sterileProblemList.length === 0 ? (
                    <div style={{ color: 'rgba(255,255,255,0.25)', textAlign: 'center', padding: '40px 0', fontSize: '0.9em' }}>ไม่มีรายการสแกน</div>
                  ) : (
                    <>
                      {sterileSuccessList.map((item, i) => {
                        const masterItem = itemMaster.find(m => m.id === item.master_id);
                        let subInfo = null;

                        if (item.details?.targetBoxName) {
                          subInfo = <span style={{ color: '#ffb74d', fontSize: '0.8em', fontWeight: '500' }}>📦 ต้องเก็บลงกล่อง: {item.details.targetBoxName}</span>;
                        }
                        else if (masterItem && (masterItem.cabinet || masterItem.shelf || masterItem.row)) {
                          const locs = [];
                          if (masterItem.cabinet) locs.push(`ตู้ ${masterItem.cabinet}`);
                          if (masterItem.shelf) locs.push(`ชั้น ${masterItem.shelf}`);
                          if (masterItem.row) locs.push(`แถว ${masterItem.row}`);
                          subInfo = <span style={{ color: '#64b5f6', fontSize: '0.8em', fontWeight: '500' }}>📍 เก็บที่: {locs.join(' ')}</span>;
                        }

                        return (
                          <div key={`success-${i}`} className="sterile-success-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', borderBottom: '1px solid rgba(255,255,255,0.05)', backgroundColor: 'rgba(46, 204, 113, 0.05)', borderRadius: '6px', marginBottom: '5px' }}>
                            <div style={{ textAlign: 'left' }}>
                              <span style={{ fontWeight: '600', color: '#ffffff', fontSize: '0.95em' }}>✅ {item.item_name || item.unique_barcode}</span>
                              {subInfo && <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>{subInfo}</div>}
                            </div>
                            <span style={{ fontSize: '0.8em', color: 'rgba(255,255,255,0.5)', marginLeft: '10px' }}>{item.scanTime}</span>
                          </div>
                        );
                      })}

                      {sterileProblemList.map((item, i) => (
                        <div key={`problem-${i}`} className="sterile-error-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', borderBottom: '1px solid rgba(255,255,255,0.05)', backgroundColor: 'rgba(231, 76, 60, 0.15)', borderRadius: '6px', marginBottom: '5px' }}>
                          <div style={{ textAlign: 'left' }}>
                            <span style={{ fontWeight: '600', color: '#ff8a80', fontSize: '0.95em' }}>❌ รหัส: {item.unique_barcode}</span>
                            <div style={{ color: '#ffb300', fontSize: '0.85em', marginTop: '2px' }}>⚠️ {item.error_msg}</div>
                          </div>
                          <span style={{ fontSize: '0.8em', color: 'rgba(255,255,255,0.5)', marginLeft: '10px' }}>{item.scanTime}</span>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Confirm Action Button */}
            <button
              onClick={handleConfirmSterileBasket}
              disabled={sterileSuccessList.length === 0}
              className="sterile-confirm-btn"
            >
              {actionType === 'bind-box' ? `ยืนยันจัดลงกล่อง (${sterileSuccessList.length} ชิ้น)` : 'ยืนยันดำเนินการ'}
            </button>

          </div>
        </div>
      )}

      {/* Popup Modal Error Alert */}
      {activeSterileErrorAlert && (
        <div className="sterile-modal-overlay" onClick={() => updateErrorAlert(null)} style={{ zIndex: 9999 }}>
          <div className="sterile-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px', textAlign: 'center', border: '1px solid rgba(255, 77, 77, 0.25)', boxShadow: '0 25px 60px rgba(0, 0, 0, 0.6), 0 0 20px rgba(255, 77, 77, 0.1)' }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              backgroundColor: 'rgba(231, 76, 60, 0.15)',
              border: '2px solid #e74c3c',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              margin: '0 auto 20px auto',
              fontSize: '28px',
              color: '#e74c3c',
              boxShadow: '0 0 10px rgba(231, 76, 60, 0.3)'
            }}>
              ✕
            </div>
            <h3 style={{
              margin: '0 0 12px 0',
              color: '#ff4d4d',
              fontSize: '1.3em',
              fontWeight: '700'
            }}>
              ข้อผิดพลาดการสแกน
            </h3>
            <p style={{
              margin: '0 0 25px 0',
              color: 'rgba(255, 255, 255, 0.85)',
              fontSize: '0.95em',
              lineHeight: '1.6',
              whiteSpace: 'pre-line'
            }}>
              {activeSterileErrorAlert}
            </p>
            <button
              onClick={() => {
                updateErrorAlert(null);
                setTimeout(() => {
                  if (barcodeInputRef && barcodeInputRef.current) barcodeInputRef.current.focus();
                }, 100);
              }}
              style={{
                width: '100%',
                padding: '14px',
                borderRadius: '12px',
                border: 'none',
                background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                color: '#ffffff',
                fontWeight: '700',
                fontSize: '1em',
                cursor: 'pointer',
                boxShadow: '0 4px 15px rgba(231, 76, 60, 0.4)',
                transition: 'all 0.2s'
              }}
            >
              ตกลง
            </button>
          </div>
        </div>
      )}

      {/* Timeline Detail Modal */}
      {activeTimelineItem && (
        <div className="sterile-modal-overlay" onClick={() => updateTimelineItem(null)} style={{ zIndex: 9999 }}>
          <div className="sterile-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '550px' }}>
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '15px' }}>
              <h3 style={{ margin: 0, fontSize: '1.4em', fontWeight: '700', color: '#fff' }}>
                📋 รายละเอียด & Timeline วัสดุ
              </h3>
              <button
                onClick={() => updateTimelineItem(null)}
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: 'none',
                  color: 'rgba(255, 255, 255, 0.75)',
                  fontSize: '1.2em',
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(231, 76, 60, 0.2)'; e.currentTarget.style.color = '#e74c3c'; }}
                onMouseOut={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)'; e.currentTarget.style.color = 'rgba(255, 255, 255, 0.75)'; }}
              >
                ✕
              </button>
            </div>

            {/* Header Info */}
            <div style={{ marginBottom: '20px' }}>
              <h2 style={{ margin: '0 0 5px 0', fontSize: '1.5em', color: '#3498db' }}>{activeTimelineItem.log?.item_name}</h2>
              {activeTimelineItem.barcode && (
                <span style={{ fontSize: '0.95em', background: 'rgba(255, 255, 255, 0.1)', padding: '6px 12px', borderRadius: '6px', fontFamily: 'monospace', color: '#ccc' }}>
                  Barcode: {activeTimelineItem.barcode}
                </span>
              )}
            </div>

            {/* Metadata Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px', marginBottom: '25px', background: 'rgba(255, 255, 255, 0.04)', padding: '20px', borderRadius: '16px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
              <div>
                <div style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.5)' }}>📅 วันที่ผลิต (Mfg Date)</div>
                <div style={{ fontSize: '1.1em', fontWeight: '600', color: '#fff', marginTop: '4px' }}>
                  {activeTimelineItem.instr?.details?.mfgDate || 'ไม่ระบุ'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.5)' }}>⏳ วันหมดอายุ (Exp Date)</div>
                <div style={{ fontSize: '1.1em', fontWeight: '600', color: '#ff7675', marginTop: '4px' }}>
                  {activeTimelineItem.instr?.details?.expDate || 'ไม่ระบุ'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.5)' }}>👤 ผู้เตรียม (Prepared By)</div>
                <div style={{ fontSize: '1.1em', fontWeight: '600', color: '#fff', marginTop: '4px' }}>
                  {activeTimelineItem.instr?.details?.preparedBy || 'ไม่ระบุ'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.5)' }}>🔍 ผู้ตรวจ (Checked By)</div>
                <div style={{ fontSize: '1.1em', fontWeight: '600', color: '#fff', marginTop: '4px' }}>
                  {activeTimelineItem.instr?.details?.checkedBy || 'ไม่ระบุ'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.5)' }}>⚙️ เครื่องที่ (Machine)</div>
                <div style={{ fontSize: '1.1em', fontWeight: '600', color: '#ffeaa7', marginTop: '4px' }}>
                  {activeTimelineItem.instr?.details?.machine || 'ไม่ระบุ'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.5)' }}>🔄 รอบที่ (Cycle)</div>
                <div style={{ fontSize: '1.1em', fontWeight: '600', color: '#ffeaa7', marginTop: '4px' }}>
                  {activeTimelineItem.instr?.details?.cycle || 'ไม่ระบุ'}
                </div>
              </div>
            </div>

            {/* Vertical Timeline */}
            <h4 style={{ color: '#3498db', marginBottom: '15px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '8px', fontSize: '1.05em' }}>
              📍 ไทม์ไลน์การเคลื่อนย้ายวัสดุ (ทั้งหมด)
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', paddingLeft: '10px', maxHeight: '250px', overflowY: 'auto', marginBottom: '25px' }}>
              {activeTimelineItem.timeline?.map((step, idx) => {
                const isCurrent = idx === activeTimelineItem.timeline.length - 1;
                return (
                  <div key={idx} style={{ display: 'flex', gap: '15px', position: 'relative' }}>
                    {idx < activeTimelineItem.timeline.length - 1 && (
                      <div style={{
                        position: 'absolute',
                        left: '9px',
                        top: '20px',
                        bottom: '-15px',
                        width: '2px',
                        background: 'rgba(255,255,255,0.15)'
                      }} />
                    )}
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      background: isCurrent ? '#2ecc71' : 'rgba(255, 255, 255, 0.2)',
                      border: isCurrent ? '4px solid rgba(46, 204, 113, 0.3)' : '3px solid rgba(255,255,255,0.08)',
                      zIndex: 1,
                      marginTop: '2px',
                      boxShadow: isCurrent ? '0 0 8px #2ecc71' : 'none'
                    }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <strong style={{ color: isCurrent ? '#2ecc71' : '#fff', fontSize: '1em' }}>
                          {step.name} {isCurrent && '(ห้องปัจจุบัน)'}
                        </strong>
                        {step.timestamp && (
                          <span style={{ fontSize: '0.8em', color: 'rgba(255,255,255,0.4)' }}>
                            {new Date(step.timestamp).toLocaleString('th-TH')}
                          </span>
                        )}
                      </div>
                      {step.action && (
                        <div style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>
                          {step.action}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              onClick={() => updateTimelineItem(null)}
              style={{
                width: '100%',
                padding: '14px',
                borderRadius: '12px',
                border: 'none',
                background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                color: '#ffffff',
                fontWeight: '700',
                fontSize: '1em',
                cursor: 'pointer',
                boxShadow: '0 4px 15px rgba(52, 152, 219, 0.4)',
                transition: 'all 0.2s'
              }}
            >
              ปิดหน้าต่าง
            </button>
          </div>
        </div>
      )}

      {/* Dispense Popup Modal */}
      {isDispenseModalOpen && (
        <div className="sterile-modal-overlay" onClick={onCloseDispenseModal} style={{ zIndex: 1100 }}>
          <div className="sterile-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '850px', background: 'linear-gradient(135deg, rgba(20, 16, 45, 0.98) 0%, rgba(36, 32, 70, 0.98) 100%)', border: '1px solid rgba(255, 255, 255, 0.15)' }}>

            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '15px' }}>
              <h3 style={{ margin: 0, fontSize: '1.4em', fontWeight: '700', color: '#fff' }}>
                📤 จ่ายของตาม Order (ห้องจ่ายเครื่องมือ)
              </h3>
              <button
                onClick={onCloseDispenseModal}
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: 'none',
                  color: 'rgba(255, 255, 255, 0.75)',
                  fontSize: '1.2em',
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(231, 76, 60, 0.2)'; e.currentTarget.style.color = '#e74c3c'; }}
                onMouseOut={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)'; e.currentTarget.style.color = 'rgba(255, 255, 255, 0.75)'; }}
              >
                ✕
              </button>
            </div>

            {/* Dispense Content */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

              {scanErrorMessage && (
                <div style={{ background: '#ff4d4d', color: '#fff', padding: '15px', borderRadius: '10px', textAlign: 'center', fontWeight: 'bold' }}>
                  ⚠️ {scanErrorMessage}
                  <button onClick={() => { if (setScanErrorMessage) setScanErrorMessage(''); }} style={{ marginLeft: '10px', cursor: 'pointer', background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', borderRadius: '4px', padding: '2px 8px' }}>ปิด</button>
                </div>
              )}

              <div style={{ background: 'rgba(255, 255, 255, 0.05)', padding: '20px', borderRadius: '15px', border: selectedOrder ? '2px solid #e65100' : '1px solid rgba(255,255,255,0.1)', color: '#fff' }}>
                <h3 style={{ color: '#ffb74d', marginTop: 0, fontSize: '1.1em' }}>📦 Order ที่กำลังจัดจ่าย</h3>
                {selectedOrder ? (
                  <>
                    <div style={{ marginBottom: '15px', padding: '15px', background: 'rgba(230, 81, 0, 0.15)', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', border: '1px solid rgba(230, 81, 0, 0.3)' }}>
                      <div>
                        <strong style={{ fontSize: '1.1em', color: '#ffb74d' }}>📍 สั่งจาก: {selectedOrder.clinic_name}</strong>
                        <div style={{ fontSize: '0.85em', color: '#ccc', marginTop: '5px' }}>⏰ เวลา: {new Date(selectedOrder.timestamp).toLocaleString('th-TH')}</div>
                        {selectedOrder.note && <div style={{ fontSize: '0.9em', color: '#ff7675', marginTop: '8px', fontWeight: 'bold' }}>📝 ข้อความ: {selectedOrder.note}</div>}
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
                      {selectedOrder.items?.map((it, idx) => {
                        const scannedQty = sterileSuccessList.filter(s => s.item_name === it.name).length;
                        const isDone = scannedQty >= it.qty;

                        const masterItem = itemMaster.find(masterLot => masterLot.item_name === it.name);
                        const locText = [];
                        if (masterItem?.cabinet) locText.push(`ตู้: ${masterItem.cabinet}`);
                        if (masterItem?.shelf) locText.push(`ชั้น: ${masterItem.shelf}`);
                        if (masterItem?.row) locText.push(`แถว: ${masterItem.row}`);
                        const locationDisplay = locText.length > 0 ? locText.join(' ') : 'ไม่ระบุพิกัด';

                        return (
                          <div key={idx} style={{ padding: '15px', borderRadius: '10px', background: isDone ? 'rgba(46, 204, 113, 0.15)' : 'rgba(255, 255, 255, 0.05)', color: isDone ? '#2ecc71' : '#fff', border: isDone ? '1px solid #2ecc71' : '1px solid rgba(255, 255, 255, 0.1)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <strong style={{ fontSize: '1em' }}>{it.name}</strong>
                              {masterItem?.image_url && (
                                <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (handleTogglePreview) handleTogglePreview(`dispense_${it.name}`); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2em' }}>🖼️</button>
                              )}
                            </div>

                            {showPreview && showPreview[`dispense_${it.name}`] && masterItem?.image_url && (
                              <div style={{ marginTop: '5px', textAlign: 'center' }}>
                                <img src={masterItem.image_url} alt={it.name} style={{ maxWidth: '100px', borderRadius: '4px', border: '1px solid #ccc' }} />
                              </div>
                            )}

                            <div style={{ fontSize: '0.8em', marginTop: '5px', marginBottom: '8px', color: isDone ? '#a5d6a7' : '#aaa' }}>
                              📍 {locationDisplay}
                            </div>

                            <span style={{ fontSize: '1.1em', fontWeight: 'bold' }}>({scannedQty}/{it.qty})</span> {isDone && '✓'}
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : <p style={{ textAlign: 'center', color: '#aaa', margin: '20px 0' }}>กรุณาเลือก Order ด้านล่างเพื่อเริ่มจัดของ</p>}
              </div>

              <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div style={{ display: 'flex', gap: '10px', filter: !selectedOrder ? 'blur(3px)' : 'none', pointerEvents: !selectedOrder ? 'none' : 'auto' }}>
                  <input
                    type="text"
                    ref={barcodeInputRef}
                    onBlur={handleBlurToFocus}
                    value={barcodeInput}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (setBarcodeInput) setBarcodeInput(val);
                      if (val.length === 13 || val.length === 14) {
                        if (handleDispenseScan) handleDispenseScan(val);
                        if (setBarcodeInput) setBarcodeInput('');
                      }
                    }}
                    placeholder="สแกนรหัส 13 หรือ 14 หลัก... (หรือรหัสกล่อง)"
                    style={{
                      flex: 1,
                      padding: '16px',
                      fontSize: '1.3em',
                      border: '2px solid #fbbf24',
                      borderRadius: '12px',
                      textAlign: 'center',
                      background: 'rgba(255,255,255,0.06)',
                      color: '#fff',
                      outline: 'none'
                    }}
                    autoFocus
                  />
                  <button onClick={() => { if (setCameraTargetField) setCameraTargetField('main'); if (setIsCameraOpen) setIsCameraOpen(true); }} style={{ padding: '0 25px', background: '#fbbf24', color: '#fff', border: 'none', borderRadius: '12px', cursor: 'pointer', fontSize: '1.8em' }}>📷</button>
                </div>

                <div style={{ display: 'flex', gap: '10px', width: '100%', filter: !selectedOrder ? 'blur(3px)' : 'none', pointerEvents: !selectedOrder ? 'none' : 'auto' }}>
                  <button
                    onClick={handleConfirmSterileBasket}
                    disabled={sterileSuccessList.length === 0 || !selectedOrder}
                    style={{
                      flex: 3,
                      padding: '16px',
                      background: (sterileSuccessList.length === 0 || !selectedOrder) ? 'rgba(255,255,255,0.08)' : '#e65100',
                      color: (sterileSuccessList.length === 0 || !selectedOrder) ? 'rgba(255,255,255,0.25)' : '#fff',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '1.2em',
                      fontWeight: 'bold',
                      cursor: (sterileSuccessList.length === 0 || !selectedOrder) ? 'not-allowed' : 'pointer',
                      boxShadow: (sterileSuccessList.length === 0 || !selectedOrder) ? 'none' : '0 4px 15px rgba(230, 81, 0, 0.4)'
                    }}
                  >
                    ยืนยันจ่ายของเข้า {selectedOrder?.clinic_name || '...'}
                  </button>

                  <button
                    onClick={() => {
                      if (setSelectedOrder) setSelectedOrder(null);
                      if (setSterileSuccessList) setSterileSuccessList([]);
                      if (setTargetWarehouse) setTargetWarehouse('');
                      if (setShowPreview) setShowPreview({});
                    }}
                    disabled={!selectedOrder}
                    style={{
                      flex: 1,
                      padding: '16px',
                      background: !selectedOrder ? 'rgba(255,255,255,0.08)' : '#7f8c8d',
                      color: !selectedOrder ? 'rgba(255,255,255,0.25)' : '#fff',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '1.1em',
                      fontWeight: 'bold',
                      cursor: !selectedOrder ? 'not-allowed' : 'pointer'
                    }}
                  >
                    ↩️ ยกเลิกการจัด
                  </button>
                </div>

                {!selectedOrder && (
                  <div style={{
                    position: 'absolute',
                    top: -5,
                    left: -5,
                    right: -5,
                    bottom: -5,
                    background: 'rgba(20, 16, 45, 0.7)',
                    backdropFilter: 'blur(6px)',
                    WebkitBackdropFilter: 'blur(6px)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: '16px',
                    zIndex: 10,
                    color: '#ffb74d',
                    fontSize: '1.4em',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    border: '1px dashed rgba(255, 183, 77, 0.35)',
                    boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
                  }}>
                    ⚠️ กรุณาเลือก Order ด้านล่างก่อนจ่ายของ
                  </div>
                )}
              </div>

              {/* Pending Orders List */}
              <div style={{ background: 'rgba(255,255,255,0.03)', padding: '20px', borderRadius: '15px', border: '1px solid rgba(255,255,255,0.06)', color: '#fff', maxHeight: '200px', overflowY: 'auto' }}>
                <h3 style={{ marginTop: 0, fontSize: '1.05em' }}>📝 รายการ Order รอจัดจ่าย</h3>
                {orders.filter(o => o.status === 'pending').sort((a, b) => getTime(a.timestamp) - getTime(b.timestamp)).map(order => (
                  <div key={order.id} style={{ border: '1px solid rgba(255,255,255,0.08)', padding: '15px', borderRadius: '10px', marginBottom: '10px', background: 'rgba(255,255,255,0.02)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                      <div>
                        <strong>📍 {order.clinic_name}</strong>
                        <div style={{ fontSize: '0.8em', color: '#aaa', marginTop: '3px' }}>⏰ {new Date(order.timestamp).toLocaleString('th-TH')}</div>
                      </div>
                      <div style={{ display: 'flex', gap: '5px' }}>
                        <button onClick={() => { if (handleCancelOrder) handleCancelOrder(order.id, order.clinic_name); }} style={{ background: 'rgba(231, 76, 60, 0.15)', border: '1px solid rgba(231, 76, 60, 0.3)', color: '#e74c3c', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85em' }}>❌ ยกเลิก</button>
                        <button onClick={() => { if (setSelectedOrder) setSelectedOrder(order); if (setTargetWarehouse) setTargetWarehouse(order.clinic_name); if (setSterileSuccessList) setSterileSuccessList([]); if (setShowPreview) setShowPreview({}); }} style={{ background: '#e65100', color: '#fff', border: 'none', padding: '6px 15px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85em' }}>📦 จัดของ</button>
                      </div>
                    </div>
                    {(() => {
                      const parsed = parseNote(order.note);
                      return (
                        <div style={{ fontSize: '0.85em', color: '#ffb74d', marginBottom: '8px', background: 'rgba(230, 81, 0, 0.1)', padding: '8px', borderRadius: '5px', border: '1px solid rgba(230, 81, 0, 0.2)', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                          <div>👤 ผู้สั่ง: <strong style={{ color: '#fff' }}>{getName(parsed.orderedBy)}</strong></div>
                          {parsed.noteText && <div>📝 ข้อความ: {parsed.noteText}</div>}
                        </div>
                      );
                    })()}
                    {order.items?.map((it, i) => <div key={i} style={{ fontSize: '0.85em', color: '#ddd' }}>- {it.name} ({it.qty})</div>)}
                  </div>
                ))}
                {orders.filter(o => o.status === 'pending').length === 0 && <p style={{ textAlign: 'center', color: '#aaa', fontSize: '0.9em' }}>ไม่มี Order ค้างจ่าย</p>}
              </div>

            </div>
          </div>
        </div>
      )}

      {/* รายการ Order รอจัดจ่าย Card */}
      <div className="sterile-card" style={{ marginTop: '20px' }}>
        <h3 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: '8px', color: '#ffb74d' }}>
          📝 รายการ Order รอจัดจ่าย ({orders.filter(o => o.status === 'pending').length})
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '15px', marginTop: '15px' }}>
          {orders.filter(o => o.status === 'pending').sort((a, b) => getTime(a.timestamp) - getTime(b.timestamp)).map(order => (
            <div key={order.id} style={{
              border: '1px solid rgba(255,255,255,0.08)',
              padding: '15px',
              borderRadius: '12px',
              background: 'rgba(255,255,255,0.02)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                  <div>
                    <strong style={{ fontSize: '1.1em', color: '#fff' }}>📍 {order.clinic_name}</strong>
                    <div style={{ fontSize: '0.8em', color: 'rgba(255,255,255,0.4)', marginTop: '3px' }}>⏰ {new Date(order.timestamp).toLocaleString('th-TH')}</div>
                  </div>
                </div>
                {(() => {
                  const parsed = parseNote(order.note);
                  return (
                    <div style={{
                      fontSize: '0.85em',
                      color: '#ffb74d',
                      marginBottom: '10px',
                      background: 'rgba(230, 81, 0, 0.1)',
                      padding: '8px',
                      borderRadius: '6px',
                      border: '1px solid rgba(230, 81, 0, 0.15)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '3px'
                    }}>
                      <div>👤 ผู้สั่ง: <strong style={{ color: '#fff' }}>{getName(parsed.orderedBy)}</strong></div>
                      {parsed.noteText && <div>📝 ข้อความ: {parsed.noteText}</div>}
                    </div>
                  );
                })()}
                <div style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.75)', marginBottom: '15px' }}>
                  {order.items?.map((it, i) => (
                    <div key={i} style={{ padding: '2px 0' }}>- {it.name} ({it.qty})</div>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => { if (handleCancelOrder) handleCancelOrder(order.id, order.clinic_name); }}
                  style={{
                    flex: 1,
                    background: 'rgba(231, 76, 60, 0.15)',
                    border: '1px solid rgba(231, 76, 60, 0.3)',
                    color: '#e74c3c',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '0.9em'
                  }}
                >
                  ❌ ยกเลิก Order
                </button>
                <button
                  onClick={() => {
                    if (setActionType) setActionType('decrease');
                    if (setSelectedOrder) setSelectedOrder(order);
                    if (setTargetWarehouse) setTargetWarehouse(order.clinic_name);
                    if (setSterileSuccessList) setSterileSuccessList([]);
                    if (setShowPreview) setShowPreview({});
                    if (setIsDispenseModalOpen) setIsDispenseModalOpen(true);
                  }}
                  style={{
                    flex: 1.5,
                    background: 'linear-gradient(135deg, #fbbf24 0%, #e65100 100%)',
                    border: 'none',
                    color: '#fff',
                    padding: '8px 15px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '0.9em',
                    boxShadow: '0 4px 10px rgba(230, 81, 0, 0.2)'
                  }}
                >
                  📦 เลือกจัดของ
                </button>
              </div>
            </div>
          ))}
          {orders.filter(o => o.status === 'pending').length === 0 && (
            <div style={{
              gridColumn: '1 / -1',
              textAlign: 'center',
              padding: '30px',
              color: 'rgba(255,255,255,0.3)',
              fontStyle: 'italic',
              background: 'rgba(255,255,255,0.01)',
              borderRadius: '12px',
              border: '1px dashed rgba(255,255,255,0.08)'
            }}>
              ไม่มี Order ค้างจ่ายในขณะนี้
            </div>
          )}
        </div>
      </div>

      {/* History Logs Card */}
      <div className="sterile-card" style={{ marginTop: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
          <h3>
            <span>📜 ประวัติงานห้อง Sterile</span>
          </h3>
          <input
            type="date"
            value={sterileHistoryDate}
            onChange={(e) => { if (setSterileHistoryDate) setSterileHistoryDate(e.target.value); }}
            className="sterile-date-picker"
          />
        </div>

        <div className="sterile-table-container">
          <table className="sterile-table">
            <thead>
              <tr>
                <th style={{ width: '120px' }}>เวลา</th>
                <th style={{ width: '220px' }}>รายการ</th>
                <th>Timeline</th>
              </tr>
            </thead>
            <tbody>
              {logs.filter(l => l.warehouse?.includes("sterile") && checkSameDate(l.timestamp, sterileHistoryDate)).map(l => {
                const barcodeMatch = l.action ? l.action.match(/\b(\d{13}|BOX-[A-Za-z0-9-]+)\b/) : null;
                const barcode = barcodeMatch ? barcodeMatch[0] : null;
                let instr = null;
                if (barcode) {
                  instr = instruments.find(i => i.unique_barcode === barcode);
                }
                if (!instr) {
                  instr = instruments.find(i => i.item_name === l.item_name && i.is_active);
                }

                const timelineSteps = resolveTimeline(instr, barcode);
                const hasMore = timelineSteps.length > 4;
                const stepsToDisplay = hasMore ? timelineSteps.slice(-4) : timelineSteps;

                return (
                  <tr key={l.id}>
                    <td style={{ fontWeight: '500', color: 'rgba(255, 255, 255, 0.6)' }}>{new Date(l.timestamp).toLocaleTimeString()}</td>
                    <td style={{ fontWeight: '600' }}>
                      {l.item_name}
                      {barcode && <div style={{ fontSize: '0.8em', color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace', marginTop: '2px' }}>{barcode}</div>}
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px', color: '#e0e0e0', fontWeight: '500' }}>
                          {stepsToDisplay.map((step, idx) => {
                            const isCurrent = idx === stepsToDisplay.length - 1;
                            return (
                              <React.Fragment key={idx}>
                                {idx > 0 && <span style={{ color: 'rgba(255,255,255,0.3)', margin: '0 4px' }}>➔</span>}
                                <span style={{
                                  color: isCurrent ? '#2ecc71' : 'rgba(255,255,255,0.85)',
                                  fontWeight: isCurrent ? '700' : 'normal',
                                  background: isCurrent ? 'rgba(46, 204, 113, 0.15)' : 'transparent',
                                  padding: isCurrent ? '2px 6px' : '0',
                                  borderRadius: isCurrent ? '4px' : '0'
                                }}>
                                  {isCurrent ? `${step.name} (ห้องปัจจุบัน)` : step.name}
                                </span>
                              </React.Fragment>
                            );
                          })}
                        </div>
                        {hasMore && (
                          <button
                            onClick={() => updateTimelineItem({ log: l, instr, timeline: timelineSteps, barcode })}
                            style={{
                              alignSelf: 'flex-start',
                              background: 'rgba(52, 152, 219, 0.15)',
                              border: '1px solid rgba(52, 152, 219, 0.3)',
                              color: '#3498db',
                              borderRadius: '6px',
                              padding: '3px 8px',
                              fontSize: '0.8em',
                              cursor: 'pointer',
                              fontWeight: '600',
                              marginTop: '3px',
                              transition: 'all 0.2s'
                            }}
                            onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(52, 152, 219, 0.25)'; }}
                            onMouseOut={(e) => { e.currentTarget.style.background = 'rgba(52, 152, 219, 0.15)'; }}
                          >
                            🔍 รายละเอียดเพิ่มเติม
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {logs.filter(l => l.warehouse?.includes("sterile") && checkSameDate(l.timestamp, sterileHistoryDate)).length === 0 && (
                <tr>
                  <td colSpan="3" style={{ textAlign: 'center', padding: '30px', color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>
                    ยังไม่มีประวัติการปฏิบัติงานในวันที่เลือก
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SupplyPage;
